// AnexoViewerModal.jsx
// Versão com suporte ao Google Drive, barra de progresso, cache em memória
// e ticket de acesso curto para os links "Abrir externamente" / "Baixar".

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  FaSpinner, FaTimes, FaDownload, FaSearchPlus,
  FaSearchMinus, FaExpand, FaFilePdf, FaFileAlt,
  FaExclamationTriangle, FaExternalLinkAlt,
} from 'react-icons/fa';
import api from '../services/api';
import './AnexoViewerModal.css';

// ─── Config ───────────────────────────────────────────────────────────────────
const API_BASE =
  import.meta.env?.VITE_API_URL ||
  process.env?.REACT_APP_API_URL ||
  '';

// Cache em memória: evita rebaixar o mesmo arquivo ao reabrir o modal
// Map<rawUrl, { objectUrl, fileName, fileType }>
const _cache = new Map();

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Testa se o valor bruto salvo no banco é um ID de arquivo do Google Drive
 * (alfanumérico + hífen/underline, 25-50 chars, sem barras).
 * Reaproveitado por resolveUrl() e pelo fluxo de ticket, para os dois
 * concordarem sempre sobre quando um anexo passa pela rota protegida do Drive.
 */
function isDriveFileId(raw) {
  return /^[a-zA-Z0-9_\-]{25,50}$/.test(String(raw || '').trim());
}

/**
 * Decide a URL de fetch baseado no que está salvo no banco:
 *   • ID do Drive (isDriveFileId)                          → /api/anexos/drive/:id
 *   • Path relativo (uploads/xxx.jpg)                       → API_BASE + path
 *   • URL absoluta                                          → como está
 */
function resolveUrl(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  if (isDriveFileId(s)) return `${API_BASE}/api/anexos-live/${s}`;
  // Path relativo
  const p = s.startsWith('/') ? s : `/${s}`;
  return `${API_BASE}${p}`;
}

function detectType(mimeType = '', url = '') {
  const m = mimeType.toLowerCase();
  const u = url.toLowerCase().split('?')[0];
  if (m.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(u)) return 'image';
  if (m === 'application/pdf'  || /\.pdf$/.test(u))                            return 'pdf';
  return 'download';
}

function extractName(url = '', headerName = '') {
  if (headerName) return decodeURIComponent(headerName);
  const parts = url.replace(/\\/g, '/').split('/');
  return decodeURIComponent(parts[parts.length - 1].split('?')[0]) || 'anexo';
}

// ─── Fetch com progresso via XHR ──────────────────────────────────────────────
function fetchWithProgress(url, onProgress, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'blob';

    // Envia o cookie HttpOnly (blb_access) no fluxo web pós-migração de auth.
    // Sem isto, em cross-origin o navegador NÃO anexa o cookie e a requisição
    // chega sem credencial → authMiddleware responde 401 "sem-header".
    // Espelha o comportamento do axios (services/api), que já usa withCredentials.
    xhr.withCredentials = true;

    // Fallback para header (impersonação / mobile / legado). Espelha a guarda do
    // services/api: só manda se for um token REAL. Componentes legados deixam
    // localStorage['token'] como a string 'null'/'undefined' → viraria
    // "Bearer null" e, como o backend é header-first, ISSO IGNORARIA o cookie
    // válido e quebraria a auth. Impersonação vive em impersonation_token.
    const rawLegacy = localStorage.getItem('token');
    const legacyToken =
      rawLegacy && rawLegacy !== 'null' && rawLegacy !== 'undefined' ? rawLegacy : null;
    const impersonationToken = sessionStorage.getItem('impersonation_token');
    const token = impersonationToken || legacyToken;
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      else onProgress(-1); // indeterminado — sem Content-Length
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const disp      = xhr.getResponseHeader('content-disposition') || '';
        const match     = disp.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        const headerName = match?.[1]?.replace(/['"]/g, '') || '';
        resolve({ blob: xhr.response, headerName, mimeType: xhr.response.type });
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };

    xhr.onerror   = () => reject(new Error('Erro de rede.'));
    xhr.ontimeout = () => reject(new Error('Tempo esgotado.'));
    xhr.timeout   = 60000;

    if (signal) signal.addEventListener('abort', () => { xhr.abort(); reject(new Error('Abortado.')); });

    xhr.send();
  });
}

// ─── Componente ───────────────────────────────────────────────────────────────
export default function AnexoViewerModal({ anexoUrl, onClose }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [fileType,  setFileType]  = useState(null);
  const [fileName,  setFileName]  = useState('anexo');
  const [loading,   setLoading]   = useState(true);
  const [progress,  setProgress]  = useState(0);  // 0–100 ou -1 (indeterminado)
  const [error,     setError]     = useState(null);
  const [zoom,      setZoom]      = useState(1);

  // ── Ações que dependem de link direto (sem header Authorization) ───────────
  // "Abrir externamente" e "Baixar" (no estado de erro) navegam de verdade,
  // então não conseguem enviar o Bearer token. Para anexos do Drive, pedimos
  // um ticket de curta duração (3 min, escopo único para este fileId) em vez
  // de expor o token de sessão inteiro numa URL.
  const [actionLoading, setActionLoading] = useState(null); // 'external' | 'download' | null
  const [actionError,   setActionError]   = useState(null);

  const abortRef    = useRef(null);
  const resolvedUrl = resolveUrl(anexoUrl);
  const driveFileId = isDriveFileId(anexoUrl) ? String(anexoUrl).trim() : null;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!resolvedUrl) return;

    // Cache hit → exibe imediatamente sem rede
    if (_cache.has(resolvedUrl)) {
      const c = _cache.get(resolvedUrl);
      setObjectUrl(c.objectUrl);
      setFileType(c.fileType);
      setFileName(c.fileName);
      setProgress(100);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setProgress(0);
    setError(null);

    fetchWithProgress(resolvedUrl, setProgress, controller.signal)
      .then(({ blob, headerName, mimeType }) => {
        const url  = URL.createObjectURL(blob);
        const type = detectType(mimeType, resolvedUrl);
        const name = extractName(resolvedUrl, headerName);
        _cache.set(resolvedUrl, { objectUrl: url, fileType: type, fileName: name });
        setObjectUrl(url);
        setFileType(type);
        setFileName(name);
        setProgress(100);
      })
      .catch((err) => {
        // Loga o motivo real (401 expirado, 404 do Drive, rede, etc.) —
        // a mensagem exibida ao usuário continua genérica de propósito.
        console.error('[AnexoViewerModal] falha ao carregar anexo:', err.message);
        if (err.message !== 'Abortado.') setError('Não foi possível carregar o anexo.');
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [resolvedUrl]);

  // ── ESC + lock scroll ──────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  // ── Zoom ───────────────────────────────────────────────────────────────────
  const zoomIn  = useCallback(() => setZoom(z => Math.min(z + 0.25, 4)),    []);
  const zoomOut = useCallback(() => setZoom(z => Math.max(z - 0.25, 0.25)), []);
  const zoomFit = useCallback(() => setZoom(1), []);
  const stopProp = useCallback(e => e.stopPropagation(), []);
  const portal   = document.getElementById('modal-root') || document.body;

  // ── Ticket de acesso (Drive) ────────────────────────────────────────────────
  const fetchTicketUrl = useCallback(async (fileId) => {
  const { data } = await api.get(`/api/anexos-live/${fileId}/ticket`);
  if (!data?.ticket) throw new Error('Ticket ausente na resposta.');
  return `${API_BASE}/api/anexos-live/ticket/${data.ticket}`;
}, []);

  const handleOpenExternally = useCallback(async () => {
    setActionError(null);

    // Path relativo ou URL absoluta: sem auth de header envolvida, link direto basta.
    if (!driveFileId) {
      window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setActionLoading('external');
    try {
      const url = await fetchTicketUrl(driveFileId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[AnexoViewerModal] falha ao gerar ticket (externo):', err.message);
      setActionError('Não foi possível gerar o link. Tente novamente.');
    } finally {
      setActionLoading(null);
    }
  }, [driveFileId, resolvedUrl, fetchTicketUrl]);

  const handleDownload = useCallback(async () => {
    setActionError(null);

    const triggerDownload = (href) => {
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    if (!driveFileId) {
      triggerDownload(resolvedUrl);
      return;
    }

    setActionLoading('download');
    try {
      const url = await fetchTicketUrl(driveFileId);
      triggerDownload(url);
    } catch (err) {
      console.error('[AnexoViewerModal] falha ao gerar ticket (download):', err.message);
      setActionError('Não foi possível baixar o arquivo. Tente novamente.');
    } finally {
      setActionLoading(null);
    }
  }, [driveFileId, resolvedUrl, fileName, fetchTicketUrl]);

  // ── Conteúdo ───────────────────────────────────────────────────────────────
  const renderContent = () => {
    if (loading) return (
      <div className="av-state av-state--loading">
        <div className="av-spinner" />
        <span>
          {progress === -1 ? 'Carregando…'
            : progress < 100 ? `Carregando… ${progress}%`
            : 'Processando…'}
        </span>
      </div>
    );

    if (error) return (
      <div className="av-state av-state--error">
        <FaExclamationTriangle />
        <span>{error}</span>
        {actionError && <span className="av-state__error-hint">{actionError}</span>}
        <div className="av-state__actions">
          <button
            type="button"
            onClick={handleOpenExternally}
            disabled={actionLoading === 'external'}
            className="av-btn av-btn--ghost"
          >
            {actionLoading === 'external' ? <FaSpinner className="av-spin-icon" /> : <FaExternalLinkAlt />}
            {' '}Abrir externamente
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={actionLoading === 'download'}
            className="av-btn av-btn--primary"
          >
            {actionLoading === 'download' ? <FaSpinner className="av-spin-icon" /> : <FaDownload />}
            {' '}Baixar
          </button>
        </div>
      </div>
    );

    if (fileType === 'image') return (
      <div className="av-image-wrap">
        <img
          src={objectUrl}
          alt={fileName}
          className="av-image"
          style={{ transform: `scale(${zoom})` }}
          draggable={false}
        />
      </div>
    );

    if (fileType === 'pdf') return (
      <iframe src={objectUrl} title={fileName} className="av-pdf" />
    );

    return (
      <div className="av-state av-state--download">
        <FaFileAlt className="av-state__icon" />
        <p className="av-state__title">Pré-visualização indisponível</p>
        <p className="av-state__sub">Este tipo de arquivo não pode ser exibido aqui.</p>
        <a href={objectUrl} download={fileName} className="av-btn av-btn--primary">
          <FaDownload /> Baixar arquivo
        </a>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="av-overlay" onClick={onClose}>
      <div className="av-modal" onClick={stopProp}>

        {/* ── Barra de progresso linear no topo ────────────────────────── */}
        <div className={`av-top-bar ${loading ? 'av-top-bar--visible' : ''}`}>
          <div
            className={`av-top-bar__fill ${progress === -1 ? 'av-top-bar__fill--indeterminate' : ''}`}
            style={{ width: progress >= 0 ? `${progress}%` : undefined }}
          />
        </div>

        {/* ── Toolbar ──────────────────────────────────────────────────── */}
        <div className="av-toolbar">
          <div className="av-toolbar__file">
            {fileType === 'pdf'
              ? <FaFilePdf className="av-toolbar__type-icon av-toolbar__type-icon--pdf" />
              : <FaFileAlt className="av-toolbar__type-icon" />}
            <span className="av-toolbar__name" title={fileName}>{fileName}</span>
          </div>

          <div className="av-toolbar__controls">
            {fileType === 'image' && !loading && !error && (
              <div className="av-zoom">
                <button onClick={zoomOut} disabled={zoom <= 0.25} className="av-icon-btn" title="Zoom −">
                  <FaSearchMinus />
                </button>
                <button onClick={zoomFit} className="av-zoom__label" title="Resetar">
                  {Math.round(zoom * 100)}%
                </button>
                <button onClick={zoomIn} disabled={zoom >= 4} className="av-icon-btn" title="Zoom +">
                  <FaSearchPlus />
                </button>
                <button onClick={zoomFit} className="av-icon-btn" title="Ajustar">
                  <FaExpand />
                </button>
                <div className="av-toolbar__sep" />
              </div>
            )}

            {objectUrl && !loading && !error && (
              <a href={objectUrl} download={fileName} className="av-icon-btn av-icon-btn--download" title="Baixar">
                <FaDownload />
              </a>
            )}

            <button onClick={onClose} className="av-icon-btn av-icon-btn--close" title="Fechar (Esc)">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* ── Conteúdo ─────────────────────────────────────────────────── */}
        <div className="av-body">{renderContent()}</div>
      </div>
    </div>,
    portal
  );
}